import { fileCapture, listInbox, today } from "@beresta/core";
import { Logo, Pressable, Text } from "@beresta/ui";
import { Suspense, lazy, useEffect, useState } from "react";
import { ComposeProvider, useCompose } from "./compose/Compose.js";
import { useDb } from "./db/provider.js";
import { t } from "./i18n.js";
import { useRouter } from "./router/index.js";
import { DayScreen } from "./screens/DayScreen.js";
import { TimeHeader } from "./shell/Header.js";
import { rememberHorizon } from "./shell/lastHorizon.js";
import { RECORD_PAGES, type Tab, tabOf, tabRoot } from "./shell/places.js";
import { Sidebar } from "./shell/Sidebar.js";
import { TabBar } from "./shell/TabBar.js";
import { Zoomed } from "./shell/Zoomed.js";
import { useZoomDirection } from "./shell/zoom.js";
import { bumpSyncRevision, useSyncRevision } from "./sync/revision.js";
import { UpdatePrompt } from "./UpdatePrompt.js";
import { DESKTOP } from "./platform/desktop.js";

// Вхід і замок ліниво: обидва тягнуть Argon2id і список BIP-39 — разом
// близько 27 КБ gzip, яких у оболонці бути не має.
const OnboardingScreen = lazy(() => import("./screens/OnboardingScreen.js").then((m) => ({ default: m.OnboardingScreen })));
const UnlockScreen = lazy(() => import("./screens/UnlockScreen.js").then((m) => ({ default: m.UnlockScreen })));
// Типовий маршрут — день. Усе інше лінивими межами: холодний старт має бюджет (ADR-0030).
const WeekScreen = lazy(() => import("./screens/WeekScreen.js").then((m) => ({ default: m.WeekScreen })));
const MonthScreen = lazy(() => import("./screens/MonthScreen.js").then((m) => ({ default: m.MonthScreen })));
const YearScreen = lazy(() => import("./screens/YearScreen.js"));
const RecordsScreen = lazy(() => import("./screens/RecordsScreen.js"));
const SearchScreen = lazy(() => import("./screens/SearchScreen.js"));
const PeopleScreen = lazy(() => import("./screens/PeopleScreen.js"));
const PersonScreen = lazy(() => import("./screens/PersonScreen.js"));

const MoreScreen = lazy(() => import("./screens/MoreScreen.js"));
const ProfileScreen = lazy(() => import("./screens/ProfileScreen.js"));
const SettingsPage = lazy(() => import("./screens/SettingsPage.js"));
const CardScreen = lazy(() => import("./screens/CardScreen.js"));

function Loading() {
  return (
    <main className="bd">
      <Text tone="ink3" style="footnote">
        {t("shell.loading")}
      </Text>
    </main>
  );
}

/**
 * Рівні вікон (WindowMap): **вхід** — без панелі, до того як архів відкрито;
 * **вкладка** й **екран у стеку** — з панеллю; **шторка** — поверх, панель
 * лишається видимою.
 */
export function App() {
  const { route } = useRouter();
  const db = useDb();

  /*
   * Візитка виходить найпершою, до всіх воріт: її відкриває чужа людина
   * без акаунта й без архіву. Нижче — онбординг чужого щоденника, у який
   * незнайомець колись і потрапляв (браузерна перевірка з двома контекстами).
   */
  if (route.kind === "card") {
    return (
      <Suspense fallback={null}>
        <CardScreen slug={route.slug} />
      </Suspense>
    );
  }

  /*
   * Сховище віддане іншій вкладці — застосунок закривається цілком. Планер,
   * у якому нічого не зберігається, гірший за порожній екран: людина писала
   * б у порожнечу. І це не помилка: архів цілий, ним користуються деінде.
   */
  if (db.status === "parked") {
    return (
      <div className="app app-solo">
        <main className="bd bd-bare entry-center">
          <span className="logo">
            <Logo size={88} />
          </span>
          <Text as="h1" style="title1">
            {t("db.parked")}
          </Text>
          <Text as="p" style="callout" tone="ink2">
            {t("db.parkedHint")}
          </Text>
          <Pressable variant="primary" wide onClick={db.takeOver}>
            {t("db.takeOver")}
          </Pressable>
        </main>
      </div>
    );
  }

  if (db.status === "ready" && db.account.status !== "unlocked") {
    return (
      <div className="app app-solo">
        <Suspense fallback={<Loading />}>
          {db.account.status === "none" ? <OnboardingScreen /> : <UnlockScreen />}
        </Suspense>
        {!DESKTOP && <UpdatePrompt />}
      </div>
    );
  }

  return (
    <ComposeProvider>
      <Shell />
    </ComposeProvider>
  );
}

function Shell() {
  const { route, go } = useRouter();
  const compose = useCompose();
  const tab = tabOf(route);

  useEffect(() => {
    if (route.kind === "horizon") rememberHorizon(route.horizon);
  }, [route]);

  const db = useDb();
  const ctx = db.status === "ready" ? db.ctx : null;

  // Щоб браузер не стер архів сам: питаємо, щойно архів відкрито. Модуль —
  // лінивий: відповідь не потрібна першому кадру.
  useEffect(() => {
    if (ctx) void import("./platform/persist.js").then((m) => m.askPersist());
  }, [ctx]);

  // Поділене з іншого застосунку ([share.ts]) — одразу записом в інбокс.
  // Позначка стирається лише після запису: збій лишає текст до наступного
  // відкриття, а не губить його.
  useEffect(() => {
    if (!ctx || !sessionStorageHas("beresta:shared")) return;
    void import("./platform/share.js").then(async ({ peekShared, clearShared }) => {
      const text = peekShared();
      if (!text) return;
      await fileCapture(ctx, { id: ctx.ids.next(), text, at: Date.now() });
      clearShared();
      bumpSyncRevision();
    });
  }, [ctx]);

  // Довжина черги інбокса — пігулка біля «Записів» у бічній панелі.
  const [inbox, setInbox] = useState(0);
  const revision = useSyncRevision();
  useEffect(() => {
    if (db.status === "ready") void listInbox(db.ctx).then((list) => setInbox(list.length));
  }, [db, revision, compose.revision, route]);

  const zoom = useZoomDirection(route.kind === "horizon" ? route.horizon.kind : null);

  /**
   * «Писати» бере рід і дату з контексту: із дня — запис того дня, із
   * нотаток — нотатка, звідусіль інде — інбокс (WindowMap).
   */
  function write() {
    if (route.kind === "horizon" && route.horizon.kind === "day")
      compose.open({ kind: "journal", date: route.horizon.key });
    else if (route.kind === "page" && route.page === "notes") compose.open({ kind: "note" });
    else compose.open({ kind: "inbox" });
  }

  const onTab = (next: Tab) => {
    const root = tabRoot(next);
    if (root === null) go({ kind: "horizon", horizon: { kind: "day", key: today().toString() } });
    else go({ kind: "page", page: root });
  };

  return (
    <div className="app">
      <Sidebar current={tab} inbox={inbox} onTab={onTab} onWrite={write} onSearch={() => go({ kind: "page", page: "search" })} />
      <div className="app-main">
      {route.kind === "horizon" ? (
        <>
          <TimeHeader
            horizon={route.horizon}
            onGo={(horizon) => go({ kind: "horizon", horizon })}
            onSearch={() => go({ kind: "page", page: "search" })}
          />
          <main className="bd">
            {route.horizon.kind === "day" ? (
              <Zoomed kind="day" zoom={zoom}>
                <DayScreen horizon={route.horizon} />
              </Zoomed>
            ) : (
              <Suspense fallback={null}>
                <Zoomed kind={route.horizon.kind} zoom={zoom}>
                  {route.horizon.kind === "week" ? (
                    <WeekScreen horizon={route.horizon} />
                  ) : route.horizon.kind === "month" ? (
                    <MonthScreen horizon={route.horizon} />
                  ) : (
                    <YearScreen horizon={route.horizon} />
                  )}
                </Zoomed>
              </Suspense>
            )}
          </main>
        </>
      ) : (
        <Suspense fallback={<Loading />}>
          {route.kind === "person" ? (
            <PersonScreen key={route.id} id={route.id} />
          ) : route.kind === "page" ? (
            (RECORD_PAGES as readonly string[]).includes(route.page) ? (
              <RecordsScreen segment={route.page as (typeof RECORD_PAGES)[number]} />
            ) : route.page === "search" ? (
              <SearchScreen />
            ) : route.page === "people" || route.page === "groups" ? (
              <PeopleScreen />
            ) : route.page === "more" ? (
              <MoreScreen />
            ) : route.page === "profile" ? (
              <ProfileScreen />
            ) : (
              <SettingsPage page={route.page} />
            )
          ) : null}
        </Suspense>
      )}

      {/* У вікні Tauri service worker немає: застосунок оновлює пакет (ADR-0047). */}
      {!DESKTOP && <UpdatePrompt overBar />}
      </div>
      <TabBar current={tab} onWrite={write} onTab={onTab} />
    </div>
  );
}

/** Перевірка без модуля: у звичайному запуску `share.js` не вантажиться зовсім. */
function sessionStorageHas(key: string): boolean {
  try {
    return sessionStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}
